
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Plus, Edit, Trash2, Users, DollarSign, Settings } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

interface SubscriptionPlan {
  id: string;
  name: string;
  type: string;
  price: number;
  max_students: number | null;
  features: any;
  stripe_price_id: string | null;
  created_at: string;
}

const AdminPlans = () => {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'pro',
    price: '',
    max_students: '',
    features: {
      calendar: true,
      workouts: true,
      training_sessions: true,
      progress: true,
      assessments: false,
      public_profile: true,
      nutrition: false,
      ai_workouts: false
    }
  });

  const queryClient = useQueryClient();

  const { data: plans, isLoading } = useQuery({
    queryKey: ['admin-subscription-plans'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('*')
        .order('price', { ascending: true });
      
      if (error) throw error;
      return data as SubscriptionPlan[];
    }
  });

  const { data: planUsage } = useQuery({
    queryKey: ['admin-plan-usage'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('subscription_plan')
        .eq('is_trainer', true)
        .not('subscription_plan', 'is', null);
      
      if (error) throw error;
      
      // Count usage by plan
      const usage: Record<string, number> = {};
      data.forEach(profile => {
        const plan = profile.subscription_plan;
        usage[plan] = (usage[plan] || 0) + 1;
      });
      
      return usage;
    }
  });

  const createPlanMutation = useMutation({
    mutationFn: async (planData: any) => {
      const { data, error } = await supabase
        .from('subscription_plans')
        .insert(planData)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-subscription-plans'] });
      setIsCreateDialogOpen(false);
      resetForm();
      toast.success('Plano criado com sucesso');
    },
    onError: (error) => {
      toast.error('Erro ao criar plano: ' + error.message);
    }
  });

  const updatePlanMutation = useMutation({
    mutationFn: async ({ id, planData }: { id: string; planData: any }) => {
      const { data, error } = await supabase
        .from('subscription_plans')
        .update(planData)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-subscription-plans'] });
      setEditingPlan(null);
      resetForm();
      toast.success('Plano atualizado com sucesso');
    },
    onError: (error) => {
      toast.error('Erro ao atualizar plano: ' + error.message);
    }
  });

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'pro',
      price: '',
      max_students: '',
      features: {
        calendar: true,
        workouts: true,
        training_sessions: true,
        progress: true,
        assessments: false,
        public_profile: true,
        nutrition: false,
        ai_workouts: false
      }
    });
  };

  const handleSubmit = () => {
    const planData = {
      name: formData.name,
      type: formData.type,
      price: parseFloat(formData.price),
      max_students: formData.max_students ? parseInt(formData.max_students) : null,
      features: formData.features
    };

    if (editingPlan) {
      updatePlanMutation.mutate({ id: editingPlan.id, planData });
    } else {
      createPlanMutation.mutate(planData);
    }
  };

  const handleEdit = (plan: SubscriptionPlan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      type: plan.type,
      price: plan.price.toString(),
      max_students: plan.max_students?.toString() || '',
      features: plan.features || {
        calendar: true,
        workouts: true,
        training_sessions: true,
        progress: true,
        assessments: false,
        public_profile: true,
        nutrition: false,
        ai_workouts: false
      }
    });
  };

  const formatPrice = (price: number) => {
    return `€${price.toFixed(2)}`;
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gestão de Planos</h1>
          <p className="text-gray-600">Criar e gerir planos de subscrição</p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => { resetForm(); setEditingPlan(null); }}>
              <Plus className="h-4 w-4 mr-2" />
              Novo Plano
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {editingPlan ? 'Editar Plano' : 'Criar Novo Plano'}
              </DialogTitle>
              <DialogDescription>
                Configure os detalhes do plano de subscrição
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Nome do Plano</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Ex: Plano Pro"
                  />
                </div>
                <div>
                  <Label htmlFor="price">Preço (€/mês)</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    placeholder="29.99"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="max_students">Máximo de Alunos</Label>
                <Input
                  id="max_students"
                  type="number"
                  value={formData.max_students}
                  onChange={(e) => setFormData({...formData, max_students: e.target.value})}
                  placeholder="Deixe vazio para ilimitado"
                />
              </div>

              <div className="space-y-3">
                <Label>Funcionalidades Incluídas</Label>
                {Object.entries(formData.features).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between">
                    <Label htmlFor={key} className="text-sm">
                      {key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ')}
                    </Label>
                    <Switch
                      id={key}
                      checked={value}
                      onCheckedChange={(checked) => 
                        setFormData({
                          ...formData, 
                          features: {...formData.features, [key]: checked}
                        })
                      }
                    />
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setIsCreateDialogOpen(false);
                setEditingPlan(null);
                resetForm();
              }}>
                Cancelar
              </Button>
              <Button onClick={handleSubmit}>
                {editingPlan ? 'Atualizar' : 'Criar'} Plano
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Planos</CardTitle>
            <Settings className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{plans?.length || 0}</div>
            <p className="text-xs text-gray-600">Planos disponíveis</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Plano Mais Popular</CardTitle>
            <Users className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {planUsage && Object.keys(planUsage).length > 0 
                ? Object.entries(planUsage).sort(([,a], [,b]) => b - a)[0][0]
                : 'N/A'}
            </div>
            <p className="text-xs text-gray-600">Mais subscrito</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Receita Potencial</CardTitle>
            <DollarSign className="h-4 w-4 text-purple-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              €{plans?.reduce((total, plan) => total + (planUsage?.[plan.type] || 0) * plan.price, 0).toFixed(2) || '0.00'}
            </div>
            <p className="text-xs text-gray-600">Receita mensal atual</p>
          </CardContent>
        </Card>
      </div>

      {/* Plans Table */}
      <Card>
        <CardHeader>
          <CardTitle>Lista de Planos</CardTitle>
          <CardDescription>
            Gerir todos os planos de subscrição disponíveis
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Preço</TableHead>
                <TableHead>Máx. Alunos</TableHead>
                <TableHead>Utilizadores</TableHead>
                <TableHead>Funcionalidades</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {plans?.map((plan) => (
                <TableRow key={plan.id}>
                  <TableCell className="font-medium">
                    {plan.name}
                    <Badge variant="outline" className="ml-2">
                      {plan.type}
                    </Badge>
                  </TableCell>
                  <TableCell>{formatPrice(plan.price)}</TableCell>
                  <TableCell>
                    {plan.max_students ? plan.max_students : 'Ilimitado'}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">
                      {planUsage?.[plan.type] || 0} utilizadores
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(plan.features || {})
                        .filter(([, enabled]) => enabled)
                        .slice(0, 3)
                        .map(([feature]) => (
                          <Badge key={feature} variant="outline" className="text-xs">
                            {feature}
                          </Badge>
                        ))}
                      {Object.values(plan.features || {}).filter(Boolean).length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{Object.values(plan.features || {}).filter(Boolean).length - 3}
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center space-x-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => {
                          handleEdit(plan);
                          setIsCreateDialogOpen(true);
                        }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPlans;
